import { z } from "zod/v4";

import { CustomEventDescriptions } from "../../descriptions/custom-event-dto.describe";
import {
  describePreventUrlSlashMatchTypes,
  PreventUrlSlashList,
  UrlMatchList,
} from "../url-match";

import { AnalyticsEventDtoSchema } from "./analytics-events";
import {
  CustomEventClickEventSettingsDtoSchema,
  CustomEventCollectionPageViewSettingsDtoSchema,
  CustomEventElementViewedSettingsDtoSchema,
  CustomEventJavascriptImportedSettingsDtoSchema,
  CustomEventJavascriptSettingsDtoSchema,
  CustomEventPageViewSettingsDtoSchema,
  CustomEventProductPageViewSettingsDtoSchema,
  CustomEventScrollDepthSettingsDtoSchema,
} from "./custom-event-settings";

export const TinybirdEventSchema = z.object({
  event_id: z.string(),
  last_event_ts: z.string().nullish(),
  n_occurrences: z.number(),
});

export type TinybirdEventType = z.infer<typeof TinybirdEventSchema>;

export const CustomEventApplyToUrlsDtoSchema = z.object({
  matchBy: z.enum(UrlMatchList),
  value: z.string(),
  error: z.string().nullish(),
});
export type CustomEventApplyToUrlsDto = z.infer<
  typeof CustomEventApplyToUrlsDtoSchema
>;

// Separate from CustomEventApplyToUrlsDtoSchema so reading pre-existing rows
// never fails on legacy data written before this check existed.
export const CustomEventApplyToUrlsInputDtoSchema =
  CustomEventApplyToUrlsDtoSchema.superRefine((url, ctx) => {
    if (
      PreventUrlSlashList.includes(url.matchBy) &&
      url.value.trim().startsWith("/")
    ) {
      ctx.addIssue({
        code: "custom",
        message: `Leading slashes are not allowed for ${describePreventUrlSlashMatchTypes()} match types. Use "${url.value.trim().replace(/^\/+/, "")}" instead.`,
        path: ["value"],
      });
    }
  });

export const CustomEventTypeList = [
  "pageView",
  "productPageView",
  "collectionPageView",
  "clickEvent",
  "elementViewed",
  "scrollDepth",
  "javascriptEvent",
  "javascriptImportedEvent",
  "addToCartCustomEvent",
  "beginCheckoutCustomEvent",
  "orderPlacedCustomEvent",
  "visitedAnyPdpCustomEvent",
  "visitedAnyCollectionCustomEvent",
] as const;

export type CustomEventType = (typeof CustomEventTypeList)[number];

type ExcludedEvents =
  | "addToCartCustomEvent"
  | "beginCheckoutCustomEvent"
  | "orderPlacedCustomEvent"
  | "visitedAnyPdpCustomEvent"
  | "visitedAnyCollectionCustomEvent";

export type CustomEventFeType = Exclude<CustomEventType, ExcludedEvents>;

export const BaseCustomEventDtoSchema = AnalyticsEventDtoSchema.extend({
  createdAtTs: z.number(),
  lastUpdateTs: z.number(),
  archivedAtTs: z.number().nullish(),
  applyToUrls: z.array(CustomEventApplyToUrlsDtoSchema).nullish(),
  experiencesList: z.array(z.string()).default([]).nullish(),
});

export type BaseCustomEventType = z.infer<typeof BaseCustomEventDtoSchema>;

export const PageViewCustomEventDtoSchema = BaseCustomEventDtoSchema.extend({
  type: z.literal("pageView"),
  settings: CustomEventPageViewSettingsDtoSchema,
});
export type PageViewCustomEventDto = z.infer<
  typeof PageViewCustomEventDtoSchema
>;

export const ProductPageViewCustomEventDtoSchema =
  BaseCustomEventDtoSchema.extend({
    type: z.literal("productPageView"),
    settings: CustomEventProductPageViewSettingsDtoSchema,
  });
export type ProductPageViewCustomEvent = z.infer<
  typeof ProductPageViewCustomEventDtoSchema
>;

export const CollectionPageViewCustomEventDtoSchema =
  BaseCustomEventDtoSchema.extend({
    type: z.literal("collectionPageView"),
    settings: CustomEventCollectionPageViewSettingsDtoSchema,
  });
export type CollectionPageViewCustomEvent = z.infer<
  typeof CollectionPageViewCustomEventDtoSchema
>;

export const ClickEventCustomEventDtoSchema = BaseCustomEventDtoSchema.extend({
  type: z.literal("clickEvent"),
  settings: CustomEventClickEventSettingsDtoSchema,
});
export type ClickEventCustomEvent = z.infer<
  typeof ClickEventCustomEventDtoSchema
>;

export const ElementViewedCustomEventDtoSchema =
  BaseCustomEventDtoSchema.extend({
    type: z.literal("elementViewed"),
    settings: CustomEventElementViewedSettingsDtoSchema,
  });
export type ElementViewedCustomEvent = z.infer<
  typeof ElementViewedCustomEventDtoSchema
>;

export const ScrollDepthCustomEventDtoSchema = BaseCustomEventDtoSchema.extend({
  type: z.literal("scrollDepth"),
  settings: CustomEventScrollDepthSettingsDtoSchema,
});
export type ScrollDepthCustomEvent = z.infer<
  typeof ScrollDepthCustomEventDtoSchema
>;

export const JavascriptEventCustomEventDtoSchema =
  BaseCustomEventDtoSchema.extend({
    type: z.literal("javascriptEvent"),
    settings: CustomEventJavascriptSettingsDtoSchema,
  });
export type JavascriptEventCustomEvent = z.infer<
  typeof JavascriptEventCustomEventDtoSchema
>;

export const JavascriptImportedEventCustomEventDtoSchema =
  BaseCustomEventDtoSchema.extend({
    type: z.literal("javascriptImportedEvent"),
    settings: CustomEventJavascriptImportedSettingsDtoSchema,
  });
export type JavascriptImportedEventCustomEvent = z.infer<
  typeof JavascriptImportedEventCustomEventDtoSchema
>;

export const CustomEventDtoSchema = z.discriminatedUnion("type", [
  PageViewCustomEventDtoSchema,
  ProductPageViewCustomEventDtoSchema,
  CollectionPageViewCustomEventDtoSchema,
  ClickEventCustomEventDtoSchema,
  ElementViewedCustomEventDtoSchema,
  ScrollDepthCustomEventDtoSchema,
  JavascriptEventCustomEventDtoSchema,
  JavascriptImportedEventCustomEventDtoSchema,
]);

export type CustomEventDto = z.infer<typeof CustomEventDtoSchema>;

const CreateAttributes = {
  id: z.uuid().optional(),
  identifier: z.string().optional(),
  error: z.string().nullish(),
  applyToUrls: z
    .array(CustomEventApplyToUrlsInputDtoSchema)
    .nullish()
    .describe(CustomEventDescriptions.applyToUrls),
};

const AUDIT_FIELDS = {
  createdAtTs: true,
  lastUpdateTs: true,
} as const;

export const CreateCustomEventDtoSchema = z.discriminatedUnion("type", [
  PageViewCustomEventDtoSchema.omit(AUDIT_FIELDS).extend(CreateAttributes),
  ProductPageViewCustomEventDtoSchema.omit(AUDIT_FIELDS).extend(
    CreateAttributes
  ),
  CollectionPageViewCustomEventDtoSchema.omit(AUDIT_FIELDS).extend(
    CreateAttributes
  ),
  ClickEventCustomEventDtoSchema.omit(AUDIT_FIELDS).extend(CreateAttributes),
  ElementViewedCustomEventDtoSchema.omit(AUDIT_FIELDS).extend(CreateAttributes),
  ScrollDepthCustomEventDtoSchema.omit(AUDIT_FIELDS).extend(CreateAttributes),
  JavascriptEventCustomEventDtoSchema.omit(AUDIT_FIELDS).extend(
    CreateAttributes
  ),
  JavascriptImportedEventCustomEventDtoSchema.omit(AUDIT_FIELDS).extend(
    CreateAttributes
  ),
]);
export type CreateCustomEventDto = z.infer<typeof CreateCustomEventDtoSchema>;

const READ_ONLY_FIELDS = {
  archivedAtTs: true,
  description: true,
  createdAtTs: true,
  lastUpdateTs: true,
  lastEventTs: true,
  eventCountLastDay: true,
} as const;

export const _PluginCustomEventSchema = z.discriminatedUnion("type", [
  PageViewCustomEventDtoSchema.omit(READ_ONLY_FIELDS),
  ProductPageViewCustomEventDtoSchema.omit(READ_ONLY_FIELDS),
  CollectionPageViewCustomEventDtoSchema.omit(READ_ONLY_FIELDS),
  ClickEventCustomEventDtoSchema.omit(READ_ONLY_FIELDS),
  ElementViewedCustomEventDtoSchema.omit(READ_ONLY_FIELDS),
  ScrollDepthCustomEventDtoSchema.omit(READ_ONLY_FIELDS),
  JavascriptEventCustomEventDtoSchema.omit(READ_ONLY_FIELDS),
  JavascriptImportedEventCustomEventDtoSchema.omit(READ_ONLY_FIELDS),
]);

type PluginOmit<T> = Omit<
  T,
  | "archivedAtTs"
  | "description"
  | "createdAtTs"
  | "lastUpdateTs"
  | "lastEventTs"
  | "eventCountLastDay"
>;

export type PluginClickEventCustomEventType = PluginOmit<ClickEventCustomEvent>;
export type PluginElementViewedCustomEventType =
  PluginOmit<ElementViewedCustomEvent>;
export type PluginScrollDepthCustomEventType =
  PluginOmit<ScrollDepthCustomEvent>;
export type PluginJavascriptEventCustomEventType =
  PluginOmit<JavascriptEventCustomEvent>;
export type PluginJavascriptImportedEventCustomEventType =
  PluginOmit<JavascriptImportedEventCustomEvent>;

export type _PluginCustomEvent = z.infer<typeof _PluginCustomEventSchema>;

export const CustomEventAnalyticsTypeList = [
  "product_views",
  "collection_views",
  "page_views",
  "custom_events",
] as const;

export type CustomEventAnalyticsType =
  (typeof CustomEventAnalyticsTypeList)[number];

export const CustomEventDefinitionSchema = z.object({
  id: z.string(), // identifier, not uuid
  field: z.enum(CustomEventAnalyticsTypeList),
  equals: z.array(z.string()).optional(),
  regexp: z.array(z.string()).optional(),
  sqlexp: z.array(z.string()).optional(),
});

export type CustomEventDefinition = z.infer<typeof CustomEventDefinitionSchema>;

export const TbEventsSchema = z.array(TinybirdEventSchema).optional();

export const PageViewWithTbEventsSchema = PageViewCustomEventDtoSchema.extend({
  tbEvents: TbEventsSchema,
});

export const ProductPageViewWithTbEventsSchema =
  ProductPageViewCustomEventDtoSchema.extend({
    tbEvents: TbEventsSchema,
  });

export const CollectionPageViewWithTbEventsSchema =
  CollectionPageViewCustomEventDtoSchema.extend({
    tbEvents: TbEventsSchema,
  });

export const ClickEventWithTbEventsSchema =
  ClickEventCustomEventDtoSchema.extend({
    tbEvents: TbEventsSchema,
  });

export const ElementViewedWithTbEventsSchema =
  ElementViewedCustomEventDtoSchema.extend({
    tbEvents: TbEventsSchema,
  });

export const ScrollDepthWithTbEventsSchema =
  ScrollDepthCustomEventDtoSchema.extend({
    tbEvents: TbEventsSchema,
  });

export const JavascriptEventWithTbEventsSchema =
  JavascriptEventCustomEventDtoSchema.extend({
    tbEvents: TbEventsSchema,
  });

export const JavascriptImportedEventWithTbEventsSchema =
  JavascriptImportedEventCustomEventDtoSchema.extend({
    tbEvents: TbEventsSchema,
  });

export const CombinedCustomEventSchema = z.discriminatedUnion("type", [
  PageViewWithTbEventsSchema,
  ProductPageViewWithTbEventsSchema,
  CollectionPageViewWithTbEventsSchema,
  ClickEventWithTbEventsSchema,
  ElementViewedWithTbEventsSchema,
  ScrollDepthWithTbEventsSchema,
  JavascriptEventWithTbEventsSchema,
  JavascriptImportedEventWithTbEventsSchema,
]);

export const DbTinybirdUnionSchema = z.union([
  CombinedCustomEventSchema,
  TinybirdEventSchema,
]);

export type CombinedCustomEvent = z.infer<typeof DbTinybirdUnionSchema>;

export const CombinedCustomEventsSchema = z.array(DbTinybirdUnionSchema);

export type CombinedCustomEvents = z.infer<typeof CombinedCustomEventsSchema>;

export const GetAllTinybirdEventsResponseBodySchema = z.object({
  custom_events: TinybirdEventSchema.array(),
});

export type GetAllTinybirdEventsResponseBodyType = z.infer<
  typeof GetAllTinybirdEventsResponseBodySchema
>;

// node backend to analytics request
export const GetAllTinybirdEventsBackendToTinybirdRequestSchema = z.object({
  shopifyDomain: z.string(),
  customEventDefinitions: CustomEventDefinitionSchema.array().optional(),
});

export type GetAllTinybirdEventsBackendToTinybirdRequestType = z.infer<
  typeof GetAllTinybirdEventsBackendToTinybirdRequestSchema
>;

export const GetAllTinybirdEventsFrontendRequestBodySchema =
  GetAllTinybirdEventsBackendToTinybirdRequestSchema.omit({
    customEventDefinitions: true,
  });

export type GetAllTinybirdEventsFrontendRequestBodyType = z.infer<
  typeof GetAllTinybirdEventsFrontendRequestBodySchema
>;
